<template>
    <div class="clients__page clients__page--view">
        <div class="min-h-inherit ">
            <div class="flex-2 w-100">
                <div>
                    <CustomText size="lg" color="gray-800" transform="capitalize" weight="semi--bold">
                        Integrations
                    </CustomText>

                    <p class="text--sm text-gray-600 mt--3">
                        Explore and connect to our available integrations. Find tools to help automate your workflows and boost productivity.
                    </p>
                </div>

                <template v-if="pageStateLoading">
                    <PageSpinner />
                </template>
                    
                <EmptyPage v-else-if="pageStateErr" btn-state="" :state="pageStateErr" @action="loadIntegrations" />
                                        
                <template v-else>
                    <div class="client__item__meta">
                        <NonLinkedTabs :list="tabList" size="md" :show-count="false" @select="setActiveTab">
                            <template #nonLinkedTab="data">
                                <NonLinkedTab :is-active="data.name === 'All Integrations'">
                                    <Allntegrations 
                                        :loading="initiateIntegrationLoading" 
                                        :selected-integration-name="selectedIntegration.name"
                                        @open-modal="setCurrentIntegration" 
                                        @initiate="initiateIntegration"
                                    />
                                </NonLinkedTab>
                                
                                <NonLinkedTab :is-active="data.name === 'Active Integrations'">
                                    <ActiveIntegrations 
                                        :loading="initiateIntegrationLoading" 
                                        :selected-integration-name="selectedIntegration.name"
                                        @open-modal="setCurrentIntegration" 
                                        @disable="openDisableWarningModal" 
                                    />
                                </NonLinkedTab>

                                <!-- <NonLinkedTab :is-active="data.name === 'Integration Settings'">
                                    <IntegrationSettings 
                                        :loading="initiateIntegrationLoading" 
                                        :selected-integration-name="selectedIntegration.name"
                                        @open-modal="setCurrentIntegration" 
                                        @disable="openDisableWarningModal" 
                                    />
                                </NonLinkedTab> -->
                            </template>
                        </NonLinkedTabs>
                    </div>
                </template>
            </div>
        </div>

        <MainModal 
            :show-modal="modalState === 'showDetailsModal'" 
            :footer="false"
            @close="closeDetailsModal"
        >
            <template #body>
                <div class="pl--10 pr--10 pb--15">
                    <div class="flex align-items-baseline justify-content-between">
                        <div class="flex">
                            <IntegrationLogos class="align-self-baseline" :name="selectedIntegration.key" />
                            <div class="ml--10">
                                <div class="flex align-items-center">
                                    <p class="text--capitalize text-gray-600 text--semi--bold text--md mr--10">
                                        {{ selectedIntegration.name }}
                                    </p>
                                    <template v-if="!availableOnPlan(selectedIntegration)">
                                        <span 
                                            class="status__map text--medium text--xs tag bold badge rounded-pill tag--pink text--upper"
                                        >
                                            <span 
                                                class="status__circle tag--pink__text" 
                                            />
                                            Paid plans only
                                        </span>
                                    </template>
                                </div>
                                <p 
                                    v-if="selectedIntegration.description" 
                                    class="text--sm text-gray-800 mt--5 integration__details__desc"
                                >
                                    {{ selectedIntegration.description }}
                                </p>
                            </div>
                        </div>
                        <div v-if="availableOnPlan(selectedIntegration)">
                            <BaseButton 
                                v-if="!selectedIntegration.enabled"
                                label="Enable" 
                                prefix-icon
                                variant="outline"
                                icon-name="add-white"
                                :loading="initiateIntegrationLoading"
                                @submit="initiateIntegration(selectedIntegration)" 
                            />
                            <BaseButton 
                                v-if="selectedIntegration.enabled"
                                label="Disable" 
                                prefix-icon
                                variant="outline"
                                variant-style="warning"
                                icon-name="no-entry"
                                :loading="initiateIntegrationLoading"
                                @submit="initiateIntegration(selectedIntegration)" 
                            />
                        </div>
                    </div>

                    <div class="mt--20">
                        <p class="text--semi--bold text--capitalize text--sm text-gray-600 mb--10">
                            Category
                        </p>
                        <div class="flex flex-wrap">
                            <p v-for="category in selectedIntegration.category" :key="category" class="integration__tier">
                                {{ category || '-' }}
                            </p>
                        </div>
                    </div>

                    <div class="mt--30">
                        <ul class="text--semi--bold text--sm text-gray-600">
                            <p class="mb--10 text-gray-600">
                                What does this integration offer?
                            </p>

                            <li v-for="offering in selectedIntegration.offerings" :key="offering" class="integration__list__item">
                                <IconSvg name="login-circle" fill="#228E9D" />
                                <span class="ml--10 text">{{ offering }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </template>
        </MainModal>

        <ConfirmDeletion
            type="integration" 
            custom-msg 
            :loading="initiateIntegrationLoading" 
            :show-modal="modalState === 'disableWarningModal'" 
            @cancel="closeDisableWarningModal" 
            @delete="disableIntegration"
        >
            <template #custom-msg>
                <p class="text--sm text-gray-800">
                    Remove this integration as part of your active integrations?
                </p>
            </template>
        </ConfirmDeletion>
    </div>
</template>

<script>
import ActiveIntegrations from "@/components/integrations/ActiveIntegrations.vue";
import Allntegrations from "@/components/integrations/Allntegrations.vue";
import IntegrationSettings from "@/components/integrations/IntegrationSettings.vue";
import { 
    IconSvg, 
    BaseButton, 
    IntegrationLogos, 
    MainModal,
    NonLinkedTab,
    NonLinkedTabs,
    ConfirmDeletion,
    EmptyPage,
    PageSpinner,
    CustomText,
} from "@/components/ui/index";

export default {
    name: "Integrations",

    components: {
        IconSvg, 
        MainModal,
        ActiveIntegrations, 
        IntegrationLogos,
        BaseButton,
        NonLinkedTab,
        NonLinkedTabs,
        Allntegrations,
        ConfirmDeletion,
        EmptyPage,
        PageSpinner,
        CustomText,
        IntegrationSettings,
    },

    data() {
        return {
            tabList: [
                {   
                    id: "allIntegrations",
                    name: "All Integrations",
                    iconName: "",
                    showCount: false,
                },
                {   
                    id: "activeIntegrations",
                    name: "Active Integrations",
                    iconName: "",
                    showCount: true,
                },
                // {   
                //     id: "integrationSettings",
                //     name: "Integration Settings",
                //     iconName: "",
                //     showCount: false,
                // },
            ],
            modalState: "",
            selectedIntegration: {
                name: "",
                description: "",
                imageUrl: "",
                allowedAccountTiers: "",
                features: "",
                offerings: "",
                defaultUrl: "",
                category: "",
            },
            initiateIntegrationLoading: false,
            currentTab: "",
            pageStateLoading: false,
            pageStateErr: "",
        };
    },

    computed: {
        subscriptionInfo() {
            return this.$store.getters["subscription/subscriptionInfo"];
        },
        
        accountPlan() {
            return this.subscriptionInfo?.plan || "free";
        },
    },


    async mounted() {
        await this.loadIntegrations({}, true);
    },

    methods: {
        setCurrentIntegration(data) {
            this.selectedIntegration = data;
            this.modalState = "showDetailsModal";
        },

        setActiveTab(tab) {
            this.currentTab = tab;
        },

        closeDetailsModal() {
            this.selectedIntegration = {
                _id: "",
                name: "",
                providerKey: "",
                description: "",
                imageUrl: "",
                allowedAccountTiers: "",
                enabled: "",
                features: "",
                offerings: "",
                defaultUrl: "",
                category: "",
            };
            this.modalState = "";
        },

        openDocs() {
            window && window.open(this.selectedIntegration.defaultUrl, "_blank");
        },

        availableOnPlan(item) {            
            if (!item) return false;

            const itemPlans = item.allowedAccountTiers;
            const isPlanIncluded = itemPlans.includes(this.accountPlan);

            if (!isPlanIncluded) return false;

            return true;
        },

        async initiateIntegration(item) {
            try {         
                this.initiateIntegrationLoading = true;
                this.selectedIntegration = item;

                const res = await this.$store.dispatch("integration/initiateIntegration", { providerKey: this.selectedIntegration.key });

                if (!res.url) {
                    this.$toast.error("Unable to initiate integration");
                    return;
                }
               
                localStorage.setItem("z:i_p", this.selectedIntegration.key);
                window.location.href = res?.url;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.initiateIntegrationLoading = false;
            }
        },

        openDisableWarningModal(item) {
            if (!item) {
                this.$toast.error("Unable to proceed");
                return;
            }
            
            this.selectedIntegration = item;
            this.modalState = "disableWarningModal";
        },

        closeDisableWarningModal() {
            this.selectedIntegration = {
                name: "",
                description: "",
                imageUrl: "",
                allowedAccountTiers: "",
                features: "",
                offerings: "",
                defaultUrl: "",
                category: "",
            };
            this.modalState = "";
        },

        async disableIntegration() {
            try {
                if (!this.selectedIntegration.enabled || !this.selectedIntegration._id) return;

                this.initiateIntegrationLoading = true;
                
                await this.$store.dispatch("integration/disableIntegration", this.selectedIntegration._id);
                this.$toast.success("Integration disabled");
                this.closeDisableWarningModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.initiateIntegrationLoading = false;
            }
        },

        async loadIntegrations(params = {}, refresh) {
            try {
                this.pageStateLoading = true;
                this.pageStateErr = "";

                await Promise.all([
                    this.$store.dispatch("config/fetchSystemIntegrations", { refresh, params }),
                    this.$store.dispatch("integration/fetchUserIntegrations", { refresh, params }),
                ]);
            } catch (error) {                
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);

                if (errMsg.includes("Network Error")) {
                    this.pageStateErr = "network_error";
                } else {
                    this.pageStateErr = "error";
                }
            } finally {
                this.pageStateLoading = false;
            }
        },
    },
};
</script>